import { mutatorAction } from 'satcheljs';
import getActiveContentTab from '../utils/getActiveContentTab';
import findSecondaryTabById from '../utils/findSecondaryTabById';
import { TabType } from '../store/schema/TabViewState';
import type SecondaryReadingPaneTabData from '../store/schema/SecondaryReadingPaneTabData';

/**
 * Updates the categories snapshot on the active secondary reading pane tab
 * so the reading pane header pills reflect the change immediately.
 *
 * @param categories The category names to add or remove.
 * @param isAdding   true → add the categories, false → remove them. Ignored when clearCategories is true.
 * @param clearCategories When true, removes all categories (categories and isAdding are ignored).
 * @param targetItemId The item ID of the secondary tab to update. Defaults to the active tab.
 */
const updateSecondaryTabCategoriesMutator: (
    categories: string[],
    isAdding: boolean,
    clearCategories: boolean,
    targetItemId?: string
) => void = mutatorAction(
    'updateSecondaryTabCategoriesMutator',
    (
        categories: string[],
        isAdding: boolean,
        clearCategories: boolean,
        targetItemId?: string
    ): void => {
        const targetTab = targetItemId ? findSecondaryTabById(targetItemId) : getActiveContentTab();
        if (targetTab?.type !== TabType.SecondaryReadingPane) {
            return;
        }
        const tabData = targetTab.data as SecondaryReadingPaneTabData;
        if (clearCategories) {
            tabData.categories = [];
            return;
        }
        let currentCategories = [...(tabData.categories ?? [])];
        if (isAdding) {
            for (const cat of categories) {
                if (!currentCategories.includes(cat)) {
                    currentCategories.push(cat);
                }
            }
        } else if (currentCategories.length) {
            currentCategories = currentCategories.filter(c => !categories.includes(c));
        }
        tabData.categories = currentCategories;
    }
);

export default updateSecondaryTabCategoriesMutator;
